import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Settings, Zap, ShieldCheck, Play, Tv, Wifi, Search, Monitor, Layout } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Configuração IPTV em Smart TVs", href: "/blog/guia-configuracao-iptv-smart-tvs" }
  ];

  const faq = [
    {
      question: "Toda Smart TV aceita IPTV?",
      answer: "Quase todas. Samsung (Tizen), LG (WebOS) e qualquer TV com Android TV/Google TV (TCL, Philips, Philco) são totalmente compatíveis. TVs mais antigas podem precisar de uma TV Box."
    },
    {
      question: "Qual o melhor app para Smart TVs?",
      answer: "Para Samsung e LG, recomendamos o IBO Player ou Bob Player. Para Android TV, o XCIPTV ou o app próprio da P2 PRO são as escolhas de elite."
    },
    {
      question: "O IPTV diminui a vida útil da Smart TV?",
      answer: "Não. O IPTV é apenas um aplicativo que roda no processador da TV, assim como a Netflix ou o YouTube. Não causa desgaste extra ao hardware."
    }
  ];

  const benefits = [
    {
      title: "Praticidade Total",
      description: "Assista milhares de canais usando apenas o controle remoto original da sua Smart TV, sem cabos extras.",
      icon: <Tv className="w-6 h-6" />
    },
    {
      title: "Alta Definição 4K",
      description: "Aproveite 100% da resolução da sua tela com nossos canais e filmes em Ultra HD nativo.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Setup Profissional",
      description: "Aprenda a configurar sua rede e aplicativos para que a TV nunca apresente lentidão nos menus.",
      icon: <Settings className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Acesso à Loja",
      description: "Navegue até a App Store oficial da sua marca de TV (Samsung Apps, LG Content Store, Play Store)."
    },
    {
      title: "Instalação do Player",
      description: "Escolha e instale o aplicativo homologado pela equipe técnica da P2 PRO."
    },
    {
      title: "Login de Elite",
      description: "Insira seus dados de acesso exclusivos no formato Xtream Codes ou Lista M3U."
    },
    {
      title: "Favoritos e Guia",
      description: "Organize sua grade de programação para um acesso rápido no dia a dia."
    }
  ];

  const diferenciais = [
    {
      title: "Especialistas em Marcas",
      description: "Nossos tutoriais cobrem as particularidades de cada fabricante (Samsung, LG, TCL).",
      icon: <Monitor className="w-6 h-6" />
    },
    {
      title: "Sinal de Baixa Latência",
      description: "Otimizamos a rota de dados especificamente para os aplicativos das Smart TVs.",
      icon: <Zap className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. O Despertar da sua Smart TV para o Entretenimento de Elite",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Sua Smart TV é muito mais do que um monitor para a TV aberta. Ela é um computador potente capaz de processar fluxos de dados massivos em tempo real. O <strong>Guia de Configuração IPTV em Smart TVs</strong> foi criado para que você explore 100% desse potencial. Quando você conecta a infraestrutura da <strong>P2 PRO</strong> à sua tela grande, a experiência de entretenimento da sua família muda de patamar.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia denso, vamos abordar as nuances de cada sistema operacional (Tizen, WebOS e Android TV). Vamos ensinar como encontrar os melhores aplicativos, como configurar o DNS para evitar bloqueios de operadoras e como ajustar a qualidade da imagem para que você tenha um cinema particular em casa. Prepare-se para uma transformação digital na sua sala de estar.
          </p>
        </div>
      )
    },
    {
      title: "2. Guia por Marca: O Que Você Precisa Saber",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Tv className="w-6 h-6 text-electric" /> Samsung & LG</h4>
              <p className="text-sm text-muted-foreground">Sistemas proprietários que exigem apps específicos da loja oficial. Foco total em estabilidade e integração com o controle remoto original.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Layout className="w-6 h-6 text-amber-500" /> Android TV & Google TV</h4>
              <p className="text-sm text-muted-foreground">Presente em TCL, Philips e Sony. Permite maior flexibilidade, instalação de apps via APK e busca por comando de voz Alexa/Google.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. O Segredo da Performance: Cabo vs Wi-Fi",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Configurar o sinal é fácil, mas manter a estabilidade em 4K exige atenção à rede:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">A Tirania do Wi-Fi 2.4GHz</h4>
              <p className="text-muted-foreground">Evite a rede 2.4GHz na sua TV. Ela sofre interferência de micro-ondas e roteadores vizinhos. Se for usar Wi-Fi, use sempre a rede 5GHz.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">A Supremacia do Cabo de Rede</h4>
              <p className="text-sm text-muted-foreground">Se sua TV está perto do roteador, use o cabo LAN. Isso elimina 99% dos travamentos 'inexplicáveis' que acontecem no meio de jogos ao vivo.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Checklist para a Configuração Perfeita",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Siga estes 3 passos técnicos após instalar o aplicativo na sua Smart TV:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Configuração de DNS Manual</h5>
                <p className="text-sm text-muted-foreground">Nas configurações de rede da TV, altere o DNS para 8.8.8.8. Isso acelera a conexão com os servidores P2 PRO em até 30%.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Ajuste de Proporção de Tela</h5>
                <p className="text-sm text-muted-foreground">No app de IPTV, defina o aspecto para 'Original' ou 'Full Screen'. Evite o 'Stretch' para não distorcer a imagem dos filmes.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Suporte via QR Code</h5>
                <p className="text-sm text-muted-foreground">Muitos apps novos geram um QR Code na tela. Aponte seu celular para ele e envie os dados para nossa equipe ativar sua TV remotamente.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias Dispositivos"
        title="Guia de Configuração IPTV em Smart TVs: O Manual Definitivo"
        subtitle="Aprenda a instalar e otimizar o IPTV nas principais marcas de TV do mercado com performance profissional."
        heroImage="https://images.unsplash.com/photo-1593305841991-05c297ba4575?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "As Smart TVs modernas são o palco ideal para o conteúdo de alta fidelidade da P2 PRO. No entanto, cada fabricante possui suas próprias regras e caminhos para a instalação de aplicativos de streaming. Saber navegar por essas diferenças é o que separa uma tarde de lazer de uma tarde de frustração técnica.",
          "Neste guia denso, vamos pegar você pela mão e ensinar o passo a passo para configurar o IPTV na sua Samsung, LG, TCL ou qualquer outra Smart TV do mercado. Vamos abordar desde a busca na loja de aplicativos até as configurações de rede avançadas que garantem que sua imagem 4K HDR nunca sofra com o buffering.",
          "Prepare-se para se tornar o mestre da tecnologia na sua casa. Com as orientações corretas da P2 PRO, sua Smart TV finalmente entregará tudo o que prometeu no dia da compra."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="💎 Sua Smart TV Merece o Melhor Sinal"
        ctaDescription="A P2 PRO é totalmente compatível com todas as marcas de Smart TV. Solicite seu teste gratuito agora mesmo."
        ctaButtonText="Testar na Minha Smart TV"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-configuracao-iptv-smart-tvs")({
  head: () => ({
    meta: [
      { title: "Guia de Configuração IPTV em Smart TVs: Completo 2026" },
      { name: "description", content: "Manual técnico para configurar IPTV em Smart TVs Samsung, LG e Android. Aprenda a instalar apps, configurar rede e garantir imagem 4K sem travamentos." },
    ],
  }),
  component: ArticlePage,
});
